import { useRouter } from '../../lib/router'
import { MarketingLayout } from './MarketingLayout'
import logoWordmark from '../../assets/logo-wordmark.svg'
import { useIsMobile } from '../../lib/useIsMobile'


const VALUES = [
  {
    title: 'WhatsApp-native, not bolted on',
    body: "We didn't take a European workshop system and add a WhatsApp widget. We designed every workflow around the reality that your customers communicate on WhatsApp. Intake, DVI delivery, quote approval, invoice, payment confirmation — all without forcing anyone to install an app.",
  },
  {
    title: 'Designed for South African conditions',
    body: "South African workshops deal with load-shedding, patchy connectivity, and workshop-floor interruptions. The mobile DVI workflow keeps inspection drafts recoverable when connectivity drops. Full browser-level offline sync is on the roadmap.",
  },
  {
    title: 'Built for the foreman, not the CTO',
    body: "We spent weeks at independent workshops in Johannesburg, Pretoria, and Cape Town watching how foremen actually work. Our interface reflects that. No 40-tab ERPs. No mandatory onboarding courses. A new user should be writing job cards within 15 minutes of first login.",
  },
  {
    title: 'Bookkeeper-friendly from day one',
    body: "SARS-compliant sequential invoices, 15% VAT calculated server-side, and complete audit trails. Your bookkeeper can work directly from the PDF invoices and month-end exports. Xero, Sage, and Zoho Books accounting sync is built in — configure your provider once in Settings.",
  },
  {
    title: 'SA-priced, SA-hosted, SA-supported',
    body: "Billing in rand — no exchange rate surprises when the dollar moves. Data in AWS Cape Town (af-south-1) — POPIA-compliant data residency without complexity. Support from a human in Johannesburg — not a ticket system that forwards to a call centre in another timezone.",
  },
]

export function AboutPage() {

  const { navigate } = useRouter()
  const isMobile = useIsMobile()

  return (
    <MarketingLayout>
      {/* ── Hero ──────────────────────────────────────────────────────────── */}
      <section style={{
        background: 'var(--navy-900, #0d1b2e)',
        padding: '96px var(--space-6) 80px',
      }}>
        <div style={{ maxWidth: 840, margin: '0 auto' }}>
          <p style={{
            fontSize: 13, fontWeight: 600, textTransform: 'uppercase',
            letterSpacing: '0.08em', color: 'var(--orange-500, #D96E1A)',
            marginBottom: 20,
          }}>About AutoAxxiz</p>
          <h1 style={{
            fontFamily: 'var(--font-display)', fontSize: 'clamp(32px, 5vw, 52px)',
            fontWeight: 700, color: '#fff', letterSpacing: '-0.03em',
            lineHeight: 1.1, marginBottom: 24,
          }}>
            Built in Johannesburg.<br />
            Built for the way South<br />
            African workshops work.
          </h1>
          <p style={{ fontSize: 18, color: 'var(--neutral-400)', lineHeight: 1.7, maxWidth: 640 }}>
            There are dozens of workshop management systems in the world. Most were built in Europe or North America and translated — badly — for South Africa. We built AutoAxxiz from scratch, in South Africa, for South Africa.
          </p>
        </div>
      </section>

      {/* ── Mission ───────────────────────────────────────────────────────── */}
      <section style={{ padding: '96px var(--space-6)', background: '#fff' }}>
        <div style={{ maxWidth: 960, margin: '0 auto', display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: isMobile ? 40 : 80, alignItems: 'center' }}>
          <div>
            <p style={{
              fontSize: 13, fontWeight: 600, textTransform: 'uppercase',
              letterSpacing: '0.08em', color: 'var(--orange-500, #D96E1A)',
              marginBottom: 16,
            }}>Our mission</p>
            <h2 style={{
              fontFamily: 'var(--font-display)', fontSize: 'clamp(26px, 4vw, 38px)',
              fontWeight: 700, color: 'var(--navy-700)', letterSpacing: '-0.02em',
              lineHeight: 1.2, marginBottom: 24,
            }}>
              Make the independent workshop as professionally equipped as any franchise — at a price that makes sense in rand.
            </h2>
            <p style={{ fontSize: 15, color: 'var(--fg-2)', lineHeight: 1.8, marginBottom: 16 }}>
              South Africa has approximately 6,500 independent vehicle workshops. The majority run on WhatsApp voice notes, paper job cards, and Excel. Not because their owners prefer it — but because every system built for them was either designed for the UK, priced in dollars, or required a dedicated IT person to run.
            </p>
            <p style={{ fontSize: 15, color: 'var(--fg-2)', lineHeight: 1.8 }}>
              AutoAxxiz exists to close that gap. We believe a two-bay independent workshop in Soweto deserves the same operational clarity as a dealer workshop in Sandton.
            </p>
          </div>
          <div>
            <div style={{
              background: 'var(--navy-700, #1A2E4A)',
              borderRadius: 20, padding: '48px 40px',
            }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 32 }}>
                {[
                  { num: '6 500+', label: 'Independent workshops in South Africa' },
                  { num: '96%', label: 'WhatsApp penetration among SA workshop customers' },
                  { num: '70%', label: 'Out-of-warranty vehicles — not franchise territory' },
                  { num: 'R 0', label: 'Per-mechanic seat tax' },
                ].map(stat => (
                  <div key={stat.num}>
                    <div style={{
                      fontFamily: 'var(--font-display)', fontSize: 36, fontWeight: 700,
                      color: '#fff', letterSpacing: '-0.03em', lineHeight: 1,
                    }}>{stat.num}</div>
                    <div style={{ fontSize: 13, color: 'var(--neutral-400)', marginTop: 4 }}>{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Why we exist — the problem ────────────────────────────────────── */}
      <section style={{ padding: '80px var(--space-6)', background: 'var(--bg-2, #f8f9fa)' }}>
        <div style={{ maxWidth: 960, margin: '0 auto' }}>
          <p style={{
            fontSize: 13, fontWeight: 600, textTransform: 'uppercase',
            letterSpacing: '0.08em', color: 'var(--orange-500, #D96E1A)',
            marginBottom: 16, textAlign: 'center',
          }}>The problem we're solving</p>
          <h2 style={{
            fontFamily: 'var(--font-display)', fontSize: 'clamp(26px, 4vw, 36px)',
            fontWeight: 700, color: 'var(--navy-700)', textAlign: 'center',
            letterSpacing: '-0.02em', marginBottom: 48,
          }}>
            The average SA workshop is drowning in WhatsApp chaos
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 24 }}>
            {[
              {
                icon: '💬',
                title: 'Quotes sent on WhatsApp, lost forever',
                body: "Customer asks for a quote. You type it out in a voice note or send a photo of a hand-written figure. They come back two weeks later and you have no record of what you quoted.",
              },
              {
                icon: '📋',
                title: 'Paper job cards nobody can read',
                body: 'Technicians write job cards by hand. Office staff retype them into Excel to invoice. Errors creep in. The mechanic who wrote it has gone home. The car is ready but the invoice takes an extra 40 minutes.',
              },
              {
                icon: '📊',
                title: "Month-end is a scramble",
                body: "At month-end you're hunting for invoices, trying to reconcile payments, and guessing who owes what. Your bookkeeper charges by the hour for the cleanup. SARS compliance is stressful because nothing is sequential.",
              },
            ].map(item => (
              <div key={item.title} style={{
                background: '#fff', border: '1px solid var(--border-subtle)',
                borderRadius: 12, padding: '28px 24px',
              }}>
                <div style={{ fontSize: 28, marginBottom: 16 }}>{item.icon}</div>
                <h3 style={{
                  fontFamily: 'var(--font-display)', fontSize: 17, fontWeight: 700,
                  color: 'var(--navy-700)', marginBottom: 10,
                }}>{item.title}</h3>
                <p style={{ fontSize: 14, color: 'var(--fg-2)', lineHeight: 1.7 }}>{item.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Our 5 values ──────────────────────────────────────────────────── */}
      <section style={{ padding: '96px var(--space-6)', background: '#fff' }}>
        <div style={{ maxWidth: 840, margin: '0 auto' }}>
          <p style={{
            fontSize: 13, fontWeight: 600, textTransform: 'uppercase',
            letterSpacing: '0.08em', color: 'var(--orange-500, #D96E1A)',
            marginBottom: 16, textAlign: 'center',
          }}>How we're different</p>
          <h2 style={{
            fontFamily: 'var(--font-display)', fontSize: 'clamp(26px, 4vw, 36px)',
            fontWeight: 700, color: 'var(--navy-700)', textAlign: 'center',
            letterSpacing: '-0.02em', marginBottom: 64,
          }}>Five things we refuse to compromise on</h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 40 }}>
            {VALUES.map((v, i) => (
              <div key={v.title} style={{ display: 'flex', gap: 28, alignItems: 'flex-start' }}>
                <div style={{
                  width: 40, height: 40, borderRadius: 10, background: 'var(--navy-700)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0,
                }}>
                  <span style={{
                    fontFamily: 'var(--font-mono)', fontSize: 14, fontWeight: 700, color: '#fff',
                  }}>0{i + 1}</span>
                </div>
                <div>
                  <h3 style={{
                    fontFamily: 'var(--font-display)', fontSize: 18, fontWeight: 700,
                    color: 'var(--navy-700)', marginBottom: 8,
                  }}>{v.title}</h3>
                  <p style={{ fontSize: 15, color: 'var(--fg-2)', lineHeight: 1.8 }}>{v.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── SA compliance ─────────────────────────────────────────────────── */}
      <section style={{ padding: '72px var(--space-6)', background: 'var(--bg-2, #f8f9fa)' }}>
        <div style={{ maxWidth: 960, margin: '0 auto' }}>
          <h2 style={{
            fontFamily: 'var(--font-display)', fontSize: 'clamp(24px, 4vw, 34px)',
            fontWeight: 700, color: 'var(--navy-700)', textAlign: 'center',
            letterSpacing: '-0.02em', marginBottom: 12,
          }}>SA compliance, not an afterthought</h2>
          <p style={{ textAlign: 'center', color: 'var(--fg-3)', fontSize: 15, marginBottom: 48 }}>
            Built in compliance with SARS, POPIA, and NCA — not retrofitted from foreign regulation.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 20 }}>
            {[
              { title: 'SARS invoices', body: 'Sequential INV-{year}-{seq} numbering, 15% VAT on every invoice, valid tax invoice fields. Ready for a SARS audit on day one.' },
              { title: 'POPIA compliant', body: 'Customer data stored in AWS af-south-1 (Cape Town). No data leaves South Africa. Information Officer contact available on request.' },
              { title: 'Works on any device', body: 'Browser-based web app — no app store, no IT admin. Your receptionist uses it on a phone, your foreman on a tablet, you on a PC.' },
              { title: 'Xero & Sage (coming soon)', body: 'Accounting sync with both Xero and Sage is in active development. Your bookkeeper works from PDF invoices until it ships.' },
            ].map(item => (
              <div key={item.title} style={{
                background: '#fff', border: '1px solid var(--border-subtle)',
                borderRadius: 12, padding: '24px 20px',
              }}>
                <h4 style={{
                  fontFamily: 'var(--font-display)', fontSize: 15, fontWeight: 700,
                  color: 'var(--navy-700)', marginBottom: 8,
                }}>{item.title}</h4>
                <p style={{ fontSize: 13, color: 'var(--fg-2)', lineHeight: 1.7 }}>{item.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Team note ─────────────────────────────────────────────────────── */}
      <section style={{ padding: '80px var(--space-6)', background: '#fff' }}>
        <div style={{ maxWidth: 760, margin: '0 auto', textAlign: 'center' }}>
          <div style={{ margin: '0 auto 24px', display: 'inline-block' }}>
            <img src={logoWordmark} alt="AutoAxxiz" style={{ height: 52 }} />
          </div>
          <h2 style={{
            fontFamily: 'var(--font-display)', fontSize: 'clamp(24px, 4vw, 34px)',
            fontWeight: 700, color: 'var(--navy-700)', letterSpacing: '-0.02em', marginBottom: 20,
          }}>AutoAxxiz is a product by JVS Ethics Lab</h2>
          <p style={{ fontSize: 15, color: 'var(--fg-2)', lineHeight: 1.8, marginBottom: 16 }}>
            AutoAxxiz is built and operated by JVS Ethics Lab. We are a small product team based in Johannesburg. Support is provided by a human who works SA business hours, not a ticket system forwarding to another continent.
          </p>
          <p style={{ fontSize: 14, color: 'var(--fg-3)', lineHeight: 1.7 }}>
            JVS Ethics Lab · Johannesburg, Gauteng<br />
            AutoAxxiz subscriptions and tax invoices are billed by JVS Ethics Lab
          </p>
        </div>
      </section>

      {/* ── CTA ───────────────────────────────────────────────────────────── */}
      <section style={{
        background: 'var(--navy-900, #0d1b2e)',
        padding: '80px var(--space-6)',
        textAlign: 'center',
      }}>
        <div style={{ maxWidth: 600, margin: '0 auto' }}>
          <h2 style={{
            fontFamily: 'var(--font-display)', fontSize: 'clamp(26px, 4vw, 40px)',
            fontWeight: 700, color: '#fff', letterSpacing: '-0.03em', marginBottom: 16,
          }}>Ready to see it in your workshop?</h2>
          <p style={{ fontSize: 16, color: 'var(--neutral-400)', marginBottom: 36 }}>
            Book a 20-minute walkthrough — we'll show you exactly how AutoAxxiz handles your workshop's workflow.
          </p>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
            <button
              onClick={() => navigate('/contact')}
              style={{
                padding: '14px 32px', borderRadius: 8, fontSize: 16, fontWeight: 600,
                background: 'var(--orange-500, #D96E1A)', border: 'none',
                color: '#fff', cursor: 'pointer',
              }}
            >Book a walkthrough</button>
            <button
              onClick={() => navigate('/pricing')}
              style={{
                padding: '14px 32px', borderRadius: 8, fontSize: 16, fontWeight: 500,
                background: 'transparent', border: '1px solid rgba(255,255,255,0.2)',
                color: 'rgba(255,255,255,0.8)', cursor: 'pointer',
              }}
            >View pricing</button>
          </div>
        </div>
      </section>
    </MarketingLayout>
  )
}
